import { z } from 'zod';

const SourceInfoSchema = z
  .object({
    type: z.string().optional().nullable(),
    sourceName: z.string().optional().nullable(),
    sourceUrl: z.string().optional().nullable(),
    date: z.string().optional().nullable(),
  })
  .optional()
  .nullable();

export const HitSchema = z.object({
  matchedName: z.string().optional().nullable(),
  dateOfBirth: z.string().optional().nullable(),
  countries: z.array(z.string()).optional().nullable(),
  matchTypes: z.array(z.string()).optional().nullable(),
  aka: z.array(z.string()).optional().nullable(),
  warnings: z.array(SourceInfoSchema).optional().nullable(),
  sanctions: z.array(SourceInfoSchema).optional().nullable(),
  pep: z.array(SourceInfoSchema).optional().nullable(),
  adverseMedia: z.array(SourceInfoSchema).optional().nullable(),
  fitnessProbity: z.array(SourceInfoSchema).optional().nullable(),
  other: z.array(SourceInfoSchema).optional().nullable(),
});

export type THit = z.infer<typeof HitSchema>;

export const AmlSchema = z.object({
  hits: z.array(HitSchema).optional().nullable(),
  createdAt: z.string().optional().nullable(),
});

export type TAml = z.infer<typeof AmlSchema>;

export type SourceInfo = z.infer<typeof SourceInfoSchema>;

export type SourceEntry = {
  date: string | undefined;
  sourceName: string | undefined;
  sourceUrl: string | undefined;
};

const getEntryFromSource = (sourceInfo: SourceInfo): SourceEntry => {
  return {
    date: sourceInfo?.date ?? undefined,
    sourceName: [sourceInfo?.sourceName, sourceInfo?.type].filter(Boolean).join(' - '),
    sourceUrl: sourceInfo?.sourceUrl ?? undefined,
  };
};

export type AmlAdapterOutput = {
  totalMatches: number;
  fullReport: TAml;
  dateOfCheck: string | undefined;
  matches: Array<{
    matchedName: string | undefined;
    dateOfBirth: string | undefined;
    countries: string;
    matchTypes: string;
    aka: string;
    pep: SourceEntry[];
    warnings: SourceEntry[];
    sanctions: SourceEntry[];
    adverseMedia: SourceEntry[];
    fitnessProbity: SourceEntry[];
    other: SourceEntry[];
  }>;
};

export const amlAdapter = (aml: TAml): AmlAdapterOutput => {
  const { hits, createdAt } = aml;

  return {
    totalMatches: hits?.length ?? 0,
    fullReport: aml,
    dateOfCheck: createdAt ?? undefined,
    matches:
      hits?.map(
        ({
          matchedName,
          dateOfBirth,
          countries,
          matchTypes,
          aka,
          sanctions,
          warnings,
          pep,
          adverseMedia,
          fitnessProbity,
          other,
        }) => ({
          matchedName: matchedName ?? undefined,
          dateOfBirth: dateOfBirth ?? undefined,
          countries: countries?.join(', ') ?? '',
          matchTypes: matchTypes?.join(', ') ?? '',
          aka: aka?.join(', ') ?? '',
          sanctions: sanctions?.filter(Boolean).map(item => getEntryFromSource(item)) ?? [],
          warnings: warnings?.filter(Boolean).map(item => getEntryFromSource(item)) ?? [],
          pep: pep?.filter(Boolean).map(item => getEntryFromSource(item)) ?? [],
          adverseMedia: adverseMedia?.filter(Boolean).map(item => getEntryFromSource(item)) ?? [],
          fitnessProbity:
            fitnessProbity?.filter(Boolean).map(item => getEntryFromSource(item)) ?? [],
          other: other?.filter(Boolean).map(item => getEntryFromSource(item)) ?? [],
        }),
      ) ?? [],
  };
};
